import { createApi } from "@reduxjs/toolkit/query/react";

import { fromBackendDate } from "@tsp-core/datetime/utils";
import { baseQuery } from "@tsp-core/store/utils";

import { ContractTypeId } from "@tsp-domains/contract-types/types";
import { parseContractTypeId } from "@tsp-domains/contract-types/utils";
import { CurrencyId } from "@tsp-domains/money/enums";
import { parseCurrencyId, transformFromStorableMoney } from "@tsp-domains/money/utils";
import { WorkItemEntityType } from "@tsp-domains/work-items/enums";

import { OverviewData } from "../types";

type LoadIncomeOverviewResponseData = {
  contractEarnings: Array<{
    contractType: ContractTypeId;
    currency: {
      id: CurrencyId;
    };
    amount: number;
  }>;
  periodEarnings: Array<{
    entityType: WorkItemEntityType;
    period: {
      year: number;
      month: number;
    };
    currency: {
      id: CurrencyId;
    };
    amount: number;
    quantity: number;
  }>;
  lastPayoutTs: number;
};

export const overviewApi = createApi({
  reducerPath: "@overview/api",
  baseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  keepUnusedDataFor: 0,
  tagTypes: ["OverviewData"],
  endpoints: (builder) => ({
    loadOverviewData: builder.query<OverviewData, void>({
      query: () => ({
        url: "/getIncomeOverview",
        method: "GET",
      }),
      transformResponse: (response: LoadIncomeOverviewResponseData): OverviewData => {
        return {
          contractEarnings: response.contractEarnings.map((contractEarning) => ({
            contractTypeId: parseContractTypeId(contractEarning.contractType),
            currencyId: parseCurrencyId(contractEarning.currency.id),
            amount: transformFromStorableMoney(contractEarning.amount),
          })),
          periodEarnings: response.periodEarnings.map((periodEarning) => ({
            entityType: periodEarning.entityType,
            currencyId: parseCurrencyId(periodEarning.currency.id),
            amount: transformFromStorableMoney(periodEarning.amount),
            period: periodEarning.period,
            quantity: periodEarning.quantity,
          })),
          lastPayout: fromBackendDate(response.lastPayoutTs),
        };
      },
    }),
  }),
});
